import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Instagram,
  Check,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { AnnouncementBar } from "../components/AnnouncementBar";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Eyebrow, SerifH } from "../components/shared";

const CONTACT_METHODS = [
  {
    Icon: MessageCircle,
    label: "WhatsApp",
    value: "+212 662 84 91 03",
    href: "https://wa.me/212662849103",
    hint: "Fastest — replies in under an hour",
  },
  {
    Icon: Phone,
    label: "Call",
    value: "+212 5 22 45 78 12",
    href: "tel:+212522457812",
    hint: "Mon–Sat · 10:00–19:00 GMT+1",
  },
  {
    Icon: Mail,
    label: "Email",
    value: "hello@yaba.mo",
    href: "mailto:hello@yaba.mo",
    hint: "Replies within 24h",
  },
];

const STUDIOS = [
  {
    label: "Fes · Atelier",
    role: "Where every jellaba is cut and hand-finished.",
    address: [
      "12 Derb El Bir, Talaa Kebira",
      "Fes El-Bali, 30000",
      "Morocco",
    ],
    hours: "By appointment only",
    image: "/yaba_mockup_06_craftsmanship.jpg",
    testId: "studio-fes",
  },
  {
    label: "Marrakech · Showroom",
    role: "Try the full collection. Meet the founders.",
    address: [
      "Rue Ibn Batouta 47, Guéliz",
      "Marrakech 40000",
      "Morocco",
    ],
    hours: "Mon–Sat · 11:00–20:00",
    image: "/yaba_carousel_01.jpg",
    testId: "studio-marrakech",
  },
];

const SUBJECTS = [
  "Order help",
  "Sizing & fit advice",
  "Custom / made-to-measure",
  "Wholesale & press",
  "Something else",
];

const FAQS = [
  {
    q: "How long does shipping take?",
    a: "EU orders arrive in 2–4 business days via DHL Express (free over €200). Worldwide takes 3–6 business days. Every order ships insured and tracked from Casablanca.",
  },
  {
    q: "Do you offer made-to-measure?",
    a: "Yes — bring us your measurements over WhatsApp and we'll cut a jellaba to your exact dimensions. Add 10–14 days to standard lead time. Price = list + 15%.",
  },
  {
    q: "What's your return policy?",
    a: "30 days from delivery, no questions, no restocking fee. Piece must be unworn with the inner label intact. We cover EU return shipping.",
  },
  {
    q: "Can I visit the atelier in Fes?",
    a: "Absolutely — but by appointment only, since the team is genuinely working. Message us on WhatsApp with a date range and we'll set something up.",
  },
];

function Field({ label, htmlFor, children, error }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span
        className="text-[10px] tracking-[0.28em] uppercase text-[#0C111C]/70"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {label}
      </span>
      {children}
      {error && (
        <span
          className="mt-1 block text-[10px] tracking-[0.2em] uppercase text-[#B95841]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {error}
        </span>
      )}
    </label>
  );
}

function FaqRow({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-b border-[#0C111C]/12" data-testid={`contact-faq-${i}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span
          className="text-base md:text-lg"
          style={{ fontFamily: "Fraunces, serif" }}
        >
          {q}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p
              className="pb-6 text-sm leading-relaxed text-[#3A3A3E] max-w-2xl"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: SUBJECTS[0],
    order: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | done

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!/.+@.+\..+/.test(form.email)) errs.email = "Enter a valid email";
    if (form.message.trim().length < 10)
      errs.message = "Message should be at least 10 characters";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");
    try {
      const store = JSON.parse(
        window.localStorage.getItem("yaba_contact_msgs") || "[]"
      );
      store.push({ ...form, ts: Date.now() });
      window.localStorage.setItem("yaba_contact_msgs", JSON.stringify(store));
    } catch {}
    setTimeout(() => setStatus("done"), 700);
  };

  return (
    <div className="bg-[#F4EDE2] text-[#0C111C] min-h-screen" data-testid="contact-page">
      <AnnouncementBar />
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-14 md:pb-20 grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-12 items-end">
          <div>
            <Eyebrow>Contact</Eyebrow>
            <SerifH className="mt-4" size="clamp(44px, 6vw, 92px)">
              Come find us —<br />
              or send a note.
            </SerifH>
            <p
              className="mt-6 text-base md:text-lg text-[#3A3A3E] max-w-lg"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              A real human answers every message. WhatsApp is fastest — under an
              hour, most days. Email if you prefer paper trails. Or come by the
              Marrakech showroom.
            </p>
          </div>

          {/* Quick contact cards */}
          <div className="grid grid-cols-1 gap-3">
            {CONTACT_METHODS.map(({ Icon, label, value, href, hint }) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={`contact-method-${label.toLowerCase()}`}
                className="group flex items-center gap-4 border border-[#0C111C]/10 bg-white/50 backdrop-blur-sm px-5 py-4 hover:border-[#C8A96A] hover:bg-white transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#0C182E] text-[#C8A96A] group-hover:bg-[#C8A96A] group-hover:text-[#0C182E] transition-colors">
                  <Icon size={18} strokeWidth={1.6} />
                </span>
                <div className="flex-1">
                  <p
                    className="text-[10px] tracking-[0.28em] uppercase text-[#0C111C]/60"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {label}
                  </p>
                  <p
                    className="text-base md:text-lg"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {value}
                  </p>
                  <p
                    className="mt-0.5 text-xs text-[#3A3A3E]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {hint}
                  </p>
                </div>
                <ChevronRight
                  size={18}
                  className="text-[#0C111C]/30 group-hover:text-[#C8A96A] group-hover:translate-x-1 transition-all"
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Form + side */}
      <section
        className="bg-[#EDE2CE] py-20 md:py-28"
        data-testid="contact-form-section"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-14 lg:gap-24">
          {/* Form */}
          <div>
            <Eyebrow>Send a note</Eyebrow>
            <SerifH className="mt-4" size="clamp(30px, 3.4vw, 52px)">
              Tell us what you need.
            </SerifH>
            <p
              className="mt-4 text-[#3A3A3E] max-w-lg"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              We read every message. If it's an order question, include your order
              number and we'll be faster.
            </p>

            <AnimatePresence mode="wait">
              {status === "done" ? (
                <motion.div
                  key="ok"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-12 border border-[#C8A96A]/50 bg-white px-8 py-10 text-center"
                  data-testid="contact-form-success"
                >
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C8A96A] text-[#0C182E]">
                    <Check size={22} strokeWidth={2} />
                  </span>
                  <h3
                    className="mt-5 text-2xl"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    Thank you, {form.name.split(" ")[0]}.
                  </h3>
                  <p
                    className="mt-2 text-sm text-[#3A3A3E]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    We received your message and will reply to {form.email}{" "}
                    within 24 hours — usually much sooner.
                  </p>
                  <Link
                    to="/"
                    className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#0C111C] underline underline-offset-4 decoration-[#C8A96A]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Keep browsing →
                  </Link>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6"
                  onSubmit={submit}
                  noValidate
                >
                  <Field label="Your name" htmlFor="c-name" error={errors.name}>
                    <input
                      id="c-name"
                      type="text"
                      value={form.name}
                      onChange={set("name")}
                      data-testid="contact-input-name"
                      placeholder="Karim R."
                      className="mt-2 w-full bg-transparent border-b border-[#0C111C]/25 focus:border-[#0C182E] outline-none py-2.5 text-base"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    />
                  </Field>
                  <Field label="Email" htmlFor="c-email" error={errors.email}>
                    <input
                      id="c-email"
                      type="email"
                      inputMode="email"
                      value={form.email}
                      onChange={set("email")}
                      data-testid="contact-input-email"
                      placeholder="you@example.com"
                      className="mt-2 w-full bg-transparent border-b border-[#0C111C]/25 focus:border-[#0C182E] outline-none py-2.5 text-base"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    />
                  </Field>
                  <Field label="Subject" htmlFor="c-subject">
                    <select
                      id="c-subject"
                      value={form.subject}
                      onChange={set("subject")}
                      data-testid="contact-input-subject"
                      className="mt-2 w-full bg-transparent border-b border-[#0C111C]/25 focus:border-[#0C182E] outline-none py-2.5 text-base appearance-none"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Order # (optional)" htmlFor="c-order">
                    <input
                      id="c-order"
                      type="text"
                      value={form.order}
                      onChange={set("order")}
                      data-testid="contact-input-order"
                      placeholder="YB-102834"
                      className="mt-2 w-full bg-transparent border-b border-[#0C111C]/25 focus:border-[#0C182E] outline-none py-2.5 text-base"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    />
                  </Field>

                  <div className="md:col-span-2">
                    <Field label="Message" htmlFor="c-message" error={errors.message}>
                      <textarea
                        id="c-message"
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        data-testid="contact-input-message"
                        placeholder="What can we help with?"
                        className="mt-2 w-full bg-transparent border-b border-[#0C111C]/25 focus:border-[#0C182E] outline-none py-2.5 text-base resize-none"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      />
                    </Field>
                  </div>

                  <div className="md:col-span-2 mt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      data-testid="contact-submit"
                      className="inline-flex items-center justify-center gap-2 bg-[#0C182E] hover:bg-[#0C111C] disabled:opacity-60 text-[#F4EDE2] px-8 h-14 text-sm tracking-[0.22em] uppercase transition-colors"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      {status === "submitting" ? "Sending…" : "Send message"}
                    </button>
                    <p
                      className="text-xs text-[#3A3A3E]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      By sending, you agree to our privacy policy. We never share
                      your details.
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* Side */}
          <aside className="space-y-8">
            <div>
              <Eyebrow>Response time</Eyebrow>
              <div className="mt-4 flex items-center gap-3">
                <span className="inline-block w-2 h-2 rounded-full bg-[#4CAF50] animate-pulse" />
                <p
                  className="text-lg"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Team online now
                </p>
              </div>
              <p
                className="mt-2 text-sm text-[#3A3A3E]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Median reply on WhatsApp: <span className="text-[#0C111C]">21 minutes</span>.
                Email: under a business day.
              </p>
            </div>

            <div>
              <Eyebrow>Hours</Eyebrow>
              <div className="mt-4 space-y-1.5 text-sm"
                   style={{ fontFamily: "Inter, sans-serif" }}>
                <div className="flex justify-between border-b border-[#0C111C]/10 py-1.5">
                  <span>Monday – Friday</span>
                  <span className="text-[#3A3A3E]">10:00 – 19:00</span>
                </div>
                <div className="flex justify-between border-b border-[#0C111C]/10 py-1.5">
                  <span>Saturday</span>
                  <span className="text-[#3A3A3E]">11:00 – 18:00</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span>Sunday</span>
                  <span className="text-[#3A3A3E]">Closed</span>
                </div>
              </div>
              <p
                className="mt-3 text-xs text-[#3A3A3E]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                All times GMT+1 (Africa/Casablanca).
              </p>
            </div>

            <div>
              <Eyebrow>Follow along</Eyebrow>
              <a
                href="https://instagram.com/yaba.official"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="mt-4 inline-flex items-center gap-3 group"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#0C182E] text-[#C8A96A] group-hover:bg-[#C8A96A] group-hover:text-[#0C182E] transition-colors">
                  <Instagram size={16} strokeWidth={1.6} />
                </span>
                <span
                  className="text-base"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  @yaba.official
                </span>
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Studios */}
      <section className="bg-[#F4EDE2] py-24 md:py-32" data-testid="contact-studios">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
          <Eyebrow>Come by</Eyebrow>
          <SerifH className="mt-4" size="clamp(30px, 3.4vw, 52px)">
            Two places you can find us.
          </SerifH>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {STUDIOS.map((s) => (
              <motion.article
                key={s.label}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid={s.testId}
                className="group"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#EDE2CE]">
                  <img
                    src={s.image}
                    alt={s.label}
                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                  />
                </div>
                <div className="mt-6 flex items-baseline justify-between gap-4">
                  <h3
                    className="text-2xl md:text-3xl"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {s.label}
                  </h3>
                  <span
                    className="text-[10px] tracking-[0.22em] uppercase text-[#C8A96A]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {s.hours}
                  </span>
                </div>
                <p
                  className="mt-2 text-sm text-[#3A3A3E]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {s.role}
                </p>
                <div
                  className="mt-4 flex items-start gap-3 text-sm text-[#0C111C]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <MapPin size={16} className="mt-0.5 text-[#C8A96A] shrink-0" />
                  <div>
                    {s.address.map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </div>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(
                    s.address.join(", ")
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#0C111C] underline underline-offset-4 decoration-[#C8A96A] hover:text-[#C8A96A]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  Open in Maps <ChevronRight size={14} />
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="bg-[#0C182E] text-[#F4EDE2] py-24 md:py-32"
        data-testid="contact-faqs"
      >
        <div className="mx-auto max-w-[1000px] px-6 md:px-12">
          <Eyebrow className="text-[#C8A96A]">Before you write</Eyebrow>
          <SerifH className="mt-4 text-[#F4EDE2]" size="clamp(30px, 3.4vw, 52px)">
            Answers we give the most.
          </SerifH>
          <div className="mt-12 border-t border-[#F4EDE2]/12">
            {FAQS.map((f, i) => (
              <div key={f.q} className="[&_button>span]:text-[#F4EDE2] [&_button]:border-[#F4EDE2]/12 [&_p]:text-[#F4EDE2]/70">
                <FaqRow q={f.q} a={f.a} i={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hours & response promise strip */}
      <section
        className="bg-[#EDE2CE] py-12"
        data-testid="contact-promise"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-xs tracking-[0.22em] uppercase"
             style={{ fontFamily: "Inter, sans-serif" }}>
          <span className="flex items-center gap-2">
            <Clock size={14} className="text-[#C8A96A]" /> Median reply · 21 min
          </span>
          <span className="text-[#0C111C]/30">·</span>
          <span className="flex items-center gap-2">
            <MessageCircle size={14} className="text-[#C8A96A]" /> Real humans, no bots
          </span>
          <span className="text-[#0C111C]/30">·</span>
          <span className="flex items-center gap-2">
            <Check size={14} className="text-[#C8A96A]" /> 30-day return promise
          </span>
        </div>
      </section>

      <Footer />
    </div>
  );
}
